/* Size quiz engine
 * Vanilla JS, no dependencies, no build step.
 *
 * Boot:  OnSizeQuiz.init(config)   // see on-size-quiz.liquid for the config shape
 * Events: document dispatches "on:size-quiz:result" with { size, alternative, measurements }
 *
 * The body profile is stored once in localStorage. Every product recalculates its own
 * recommendation from that profile against its own size chart, so a shopper answers
 * the questions one time and gets a size on all 17 products.
 */
(function (window, document) {
  'use strict';

  var STORAGE_KEY = 'on_sq_body_v2';

  /* ------------------------------------------------------------------ *
   * Fallback copy. The Liquid snippet overrides all of this with        *
   * Shopify translations so the quiz works in all 12 markets.           *
   * ------------------------------------------------------------------ */
  var SV = {
    trigger: 'Hitta din storlek',
    trigger_known: 'Din storlek: {size}',
    brand: 'OUTDOORS NORDIC',
    close: 'Stäng',
    intro_title: 'Rätt storlek första gången',
    intro_body: 'Svara på sex frågor så räknar vi ut vilken storlek som sitter bäst på just dig.',
    strip: 'Klart på 30 sekunder',
    back_to_top: 'Till toppen',
    privacy: 'Integritetspolicy',
    next: 'Nästa',
    done: 'Visa min storlek',
    height: 'Längd',
    height_q: 'Hur lång är du?',
    height_value: 'Jag är {value}',
    weight: 'Vikt',
    weight_q: 'Vad väger du?',
    weight_value: 'Jag väger {value}',
    age: 'Ålder',
    age_q: 'Hur gammal är du?',
    age_help: 'Kroppen ändrar form med åren. Det påverkar vilken storlek som sitter bäst.',
    age_value: 'Jag är {value}',
    waist: 'Midja',
    waist_q: 'Hur ser din midja ut?',
    waist_flat: 'Platt',
    waist_average: 'Genomsnittlig',
    waist_round: 'Rund',
    hips: 'Höfter',
    hips_q: 'Hur ser dina höfter ut?',
    hips_narrow: 'Smala',
    hips_average: 'Genomsnittliga',
    hips_wide: 'Bredare',
    legs: 'Ben',
    legs_q: 'Hur är dina ben jämfört med andra som är lika långa?',
    legs_short: 'Kortare',
    legs_average: 'Som de flesta',
    legs_long: 'Längre',
    bust: 'Byst',
    bust_q: 'Hur ser din byst ut?',
    bust_small: 'Liten',
    bust_average: 'Genomsnittlig',
    bust_full: 'Fylligare',
    fit: 'Passform',
    fit_q: 'Hur vill du att plagget sitter?',
    fit_tight: 'Tajtare',
    fit_regular: 'Som vanligt',
    fit_loose: 'Lösare',
    result_title: 'Din storlek',
    result_sub: 'Vi sparar svaren så du får storleksråd på alla plagg.',
    result_for: 'För den här produkten rekommenderar vi',
    out_of_range: 'Den här modellen räcker inte hela vägen till dina mått. Hör av dig så hjälper vi dig hitta en som gör det.',
    contact_link: 'Kontakta oss',
    color: 'Färg',
    pick_size: 'Välj {size}',
    add: 'Lägg {size} i varukorgen',
    sold_out: 'Slut i {size}',
    adding: 'Lägger till ...',
    added: 'Tillagd i varukorgen',
    cart_error: 'Det gick inte att lägga varan i varukorgen. Försök igen.',
    restart: 'Gör om quizet',
    size_guide: 'Se hela storleksguiden',
    cm: 'cm', ft: 'ft', kg: 'kg', lb: 'lb',
    note: 'Rätt storlek från början betyder färre returer. Det tjänar du på, och miljön med.'
  };

  /* ------------------------------------------------------------------ *
   * Figures                                                             *
   * ------------------------------------------------------------------ */
  /* Figurerna ritas symmetriskt ur nagra breddvarden i stallet for handritade
     halvkroppar. Poangen ar att man ska SE vilket matt fragan galler: kroppen
     ar likadan i alla tre alternativen, det enda som andras ar bredden pa den
     niva fragan handlar om, och dar ligger ett markerat matband. */
  var FIG_W = 100;
  var FIG_H = 150;
  var FIG_Y = { shoulder: 30, bust: 52, waist: 80, hip: 106, hem: 142 };

  var FIG_SHAPES = {
    waist: {
      level: 'waist',
      flat: { shoulder: 40, bust: 36, waist: 26, hip: 40 },
      average: { shoulder: 40, bust: 36, waist: 31, hip: 40 },
      round: { shoulder: 40, bust: 37, waist: 38, hip: 41 }
    },
    hips: {
      level: 'hip',
      narrow: { shoulder: 40, bust: 36, waist: 30, hip: 34 },
      average: { shoulder: 40, bust: 36, waist: 30, hip: 41 },
      wide: { shoulder: 40, bust: 36, waist: 31, hip: 49 }
    },
    bust: {
      level: 'bust',
      small: { shoulder: 38, bust: 31, waist: 30, hip: 40 },
      average: { shoulder: 40, bust: 36, waist: 30, hip: 40 },
      full: { shoulder: 41, bust: 43, waist: 31, hip: 40 }
    }
  };

  function figPath(w) {
    var cx = FIG_W / 2;
    var y = FIG_Y;
    var sh = w.shoulder / 2, bu = w.bust / 2, wa = w.waist / 2, hi = w.hip / 2;
    var hemHalf = hi - 3;
    var p = [];
    p.push('M' + (cx - sh) + ' ' + y.shoulder);
    p.push('C' + (cx - bu - 1) + ' ' + (y.shoulder + 8) + ' ' + (cx - bu) + ' ' + (y.bust - 8) + ' ' + (cx - bu) + ' ' + y.bust);
    p.push('C' + (cx - bu) + ' ' + (y.bust + 11) + ' ' + (cx - wa) + ' ' + (y.waist - 11) + ' ' + (cx - wa) + ' ' + y.waist);
    p.push('C' + (cx - wa) + ' ' + (y.waist + 11) + ' ' + (cx - hi) + ' ' + (y.hip - 13) + ' ' + (cx - hi) + ' ' + y.hip);
    p.push('C' + (cx - hi) + ' ' + (y.hip + 16) + ' ' + (cx - hemHalf) + ' ' + (y.hem - 12) + ' ' + (cx - hemHalf) + ' ' + y.hem);
    p.push('L' + (cx + hemHalf) + ' ' + y.hem);
    p.push('C' + (cx + hemHalf) + ' ' + (y.hem - 12) + ' ' + (cx + hi) + ' ' + (y.hip + 16) + ' ' + (cx + hi) + ' ' + y.hip);
    p.push('C' + (cx + hi) + ' ' + (y.hip - 13) + ' ' + (cx + wa) + ' ' + (y.waist + 11) + ' ' + (cx + wa) + ' ' + y.waist);
    p.push('C' + (cx + wa) + ' ' + (y.waist - 11) + ' ' + (cx + bu) + ' ' + (y.bust + 11) + ' ' + (cx + bu) + ' ' + y.bust);
    p.push('C' + (cx + bu) + ' ' + (y.bust - 8) + ' ' + (cx + sh + 1) + ' ' + (y.shoulder + 8) + ' ' + (cx + sh) + ' ' + y.shoulder);
    p.push('Z');
    return p.join('');
  }

  /* Benfiguren ritas for sig: har ar det inte bredden pa en niva som andras utan
     benens LANGD, och matbandet ligger langs innerbenet i stallet for runt kroppen. */
  var FIG_LEG_HEM = { short: 126, average: 136, long: 146 };

  function figureLegs(key) {
    var cx = FIG_W / 2;
    var y = FIG_Y;
    var hem = FIG_LEG_HEM[key] || FIG_LEG_HEM.average;
    var w = { shoulder: 40, bust: 36, waist: 30, hip: 40 };
    var sh = w.shoulder / 2, bu = w.bust / 2, wa = w.waist / 2, hi = w.hip / 2;
    var crotch = y.hip + 10;
    var gap = 3, ankle = 5;

    var out = '<svg viewBox="0 0 ' + FIG_W + ' ' + FIG_H + '" aria-hidden="true" focusable="false">';
    out += '<circle class="on-sq-fig-line" cx="' + cx + '" cy="14" r="9"/>';
    out += '<path class="on-sq-fig-line" d="M' + (cx - 5) + ' 23 L' + (cx - 5) + ' 30 M' + (cx + 5) + ' 23 L' + (cx + 5) + ' 30"/>';
    // bal ner till grenen
    var t = 'M' + (cx - sh) + ' ' + y.shoulder +
      'C' + (cx - bu - 1) + ' ' + (y.shoulder + 8) + ' ' + (cx - bu) + ' ' + (y.bust - 8) + ' ' + (cx - bu) + ' ' + y.bust +
      'C' + (cx - bu) + ' ' + (y.bust + 11) + ' ' + (cx - wa) + ' ' + (y.waist - 11) + ' ' + (cx - wa) + ' ' + y.waist +
      'C' + (cx - wa) + ' ' + (y.waist + 11) + ' ' + (cx - hi) + ' ' + (y.hip - 13) + ' ' + (cx - hi) + ' ' + y.hip +
      'L' + (cx - hi) + ' ' + crotch + 'L' + (cx + hi) + ' ' + crotch + 'L' + (cx + hi) + ' ' + y.hip +
      'C' + (cx + hi) + ' ' + (y.hip - 13) + ' ' + (cx + wa) + ' ' + (y.waist + 11) + ' ' + (cx + wa) + ' ' + y.waist +
      'C' + (cx + wa) + ' ' + (y.waist - 11) + ' ' + (cx + bu) + ' ' + (y.bust + 11) + ' ' + (cx + bu) + ' ' + y.bust +
      'C' + (cx + bu) + ' ' + (y.bust - 8) + ' ' + (cx + sh + 1) + ' ' + (y.shoulder + 8) + ' ' + (cx + sh) + ' ' + y.shoulder + 'Z';
    out += '<path class="on-sq-fig-line on-sq-fig-body" d="' + t + '"/>';
    // ben, avsmalnande mot fotknolen
    out += '<path class="on-sq-fig-line on-sq-fig-body" d="M' + (cx - hi) + ' ' + crotch +
      ' L' + (cx - gap) + ' ' + crotch + ' L' + (cx - gap - 1) + ' ' + hem +
      ' L' + (cx - gap - 1 - ankle * 2) + ' ' + hem + ' Z"/>';
    out += '<path class="on-sq-fig-line on-sq-fig-body" d="M' + (cx + hi) + ' ' + crotch +
      ' L' + (cx + gap) + ' ' + crotch + ' L' + (cx + gap + 1) + ' ' + hem +
      ' L' + (cx + gap + 1 + ankle * 2) + ' ' + hem + ' Z"/>';
    // matbandet langs innerbenet
    out += '<path class="on-sq-fig-mark" d="M' + (cx - 10) + ' ' + crotch + ' V' + hem + '"/>';
    out += '<path class="on-sq-fig-mark" d="M' + (cx - 14) + ' ' + crotch + ' H' + (cx - 6) +
      ' M' + (cx - 14) + ' ' + hem + ' H' + (cx - 6) + '"/>';
    return out + '</svg>';
  }

  function figure(group, key) {
    if (group === 'legs') return figureLegs(key);
    var set = FIG_SHAPES[group];
    var w = set[key];
    var cx = FIG_W / 2;
    var level = set.level;
    var y = FIG_Y[level];
    var half = w[level] / 2;
    var reach = half + 7;

    var out = '<svg viewBox="0 0 ' + FIG_W + ' ' + FIG_H + '" aria-hidden="true" focusable="false">';
    // huvud och hals, sa silhuetten last som en kropp och inte som en vas
    out += '<circle class="on-sq-fig-line" cx="' + cx + '" cy="14" r="9"/>';
    out += '<path class="on-sq-fig-line" d="M' + (cx - 5) + ' 23 L' + (cx - 5) + ' 30 M' + (cx + 5) + ' 23 L' + (cx + 5) + ' 30"/>';
    out += '<path class="on-sq-fig-line on-sq-fig-body" d="' + figPath(w) + '"/>';
    // matbandet: heldragen linje med andmarkeringar pa exakt den niva fragan galler
    out += '<path class="on-sq-fig-mark" d="M' + (cx - reach) + ' ' + y + ' H' + (cx + reach) + '"/>';
    out += '<path class="on-sq-fig-mark" d="M' + (cx - reach) + ' ' + (y - 4) + ' V' + (y + 4) +
           ' M' + (cx + reach) + ' ' + (y - 4) + ' V' + (y + 4) + '"/>';
    return out + '</svg>';
  }

  /* ------------------------------------------------------------------ *
   * Body estimation                                                     *
   *                                                                     *
   * Circumferences are estimated from height, weight, age and self-      *
   * reported shape. The coefficients below are a starting point, not     *
   * ground truth. Calibrate them against real return-reason data.        *
   * ------------------------------------------------------------------ */
  var AGE_MID = { '18-24': 21, '25-34': 30, '35-44': 40, '45-54': 50, '55-64': 60, '65+': 70 };

  var SHAPE = {
    waist: { flat: -4.5, average: 0, round: 5.5 },
    hips: { narrow: -3.5, average: 0, wide: 4.5 },
    bust: { small: -4.0, average: 0, full: 5.0 }
  };

  var FIT_OFFSET = { tight: -1.5, regular: 0, loose: 2.5 };

  function estimateBody(a) {
    var h = a.height;
    var bmi = a.weight / Math.pow(h / 100, 2);
    var age = AGE_MID[a.age] || 40;
    var over30 = Math.max(0, age - 30);

    /* Midjan vaxer INTE linjart med BMI hela vagen. Den gamla raka linjen gav
       112 cm vid BMI 37,5 - flera cm over verkligheten - och slog darmed ut
       kraftiga kunder ur tabellen helt. Over BMI 27 planar kurvan ut. */
    var bmiWaist = bmi <= 27 ? 2.30 * bmi : 2.30 * 27 + 1.45 * (bmi - 27);

    var m = {
      waist: bmiWaist + 22.0 + over30 * 0.13 + (h - 168) * -0.18,
      hip: 2.05 * bmi + 51.0 - over30 * 0.04 + (h - 168) * -0.12,
      bust: 1.95 * bmi + 45.0 + (h - 168) * -0.10
    };

    m.waist += SHAPE.waist[a.waist] || 0;
    m.hip += SHAPE.hips[a.hips] || 0;
    m.bust += SHAPE.bust[a.bust] || 0;

    m.bmi = bmi;
    return m;
  }

  function scoreSizes(sizes, m, fitOffset, dims) {
    var scored = [];
    for (var i = 0; i < sizes.length; i++) {
      var s = sizes[i];
      var penalty = 0;
      var usedWeight = 0;
      for (var d = 0; d < dims.length; d++) {
        var key = dims[d].key;
        var range = s[key];
        if (!range || m[key] == null) continue;
        var v = m[key] + fitOffset;
        var lo = range[0], hi = range[1];
        var half = Math.max((hi - lo) / 2, 1);
        /* Osymmetriskt med flit. Nastan alla plagg har resar eller stretch i
           midjan, sa att ligga OVER spannet kostar lite - tyget ger efter. Att
           ligga UNDER betyder daremot ett plagg som hanger, och det gar inte att
           tanja bort. Returerna i butiken handlar om benlangd, inte om midja och
           hoft, sa girth ska inte kasta upp kunden flera storlekar i onodan. */
        var p;
        if (v < lo) p = 1 + (lo - v) / 2.5;
        else if (v > hi) p = 0.4 + (v - hi) / 4;
        else p = (Math.abs(v - (lo + hi) / 2) / half) * 0.6;
        penalty += p * dims[d].weight;
        usedWeight += dims[d].weight;
      }
      if (usedWeight === 0) continue;
      scored.push({ index: i, size: s, penalty: penalty / usedWeight });
    }
    scored.sort(function (a, b) { return a.penalty - b.penalty; });
    return scored;
  }

  /* BENLANGDEN ar ett GOLV, inte ett tiebreak.
     Butikens returer kommer fran att byxorna ar for korta, inte fran midja och
     hoft (nastan allt ar elastiskt i linningen). Darfor far langden bestamma den
     MINSTA storlek kunden kan fa: hittar vi ingen tillrackligt lang byxa i den
     storlek girth pekar ut, hojs storleken tills innerbenet racker. Nedgradering
     for korta ben sker aldrig - en for lang byxa gar att vika upp, en for kort
     gar inte att forlanga.
     Faktorn ar kalibrerad mot butikens egen tabell: 175 cm bar 2XL pa QuickDry
     (71 cm innerben), alltsa 71/175 = 0,406. Att tabellens langdkolumn ar graderad
     for kortare personer an vad midjekolumnen antyder ar just det kunderna klagar
     pa - modellen ska folja tabellen, inte tvartom. */
  var LEG_RATIO = 0.406;
  /* Halva steget mellan tva storlekar (~1,5 cm) accepteras som for kort. Lagre varde
     ger tvasteg-hopp vid tabellgranser, hogre varde gor byxan for kort. */
  var LEG_TOL = 1.5;

  /* Benfragan justerar behovet i cm. OSYMMETRISKT med flit: "langre ben" lyfter mer
     an "kortare ben" sanker, for att en byxa som blir en aning for lang gar att vika
     upp medan en for kort blir en retur. Svaret ar en sjalvskattning och far darfor
     inte kunna gora storre skada an sa. */
  var LEG_BIAS = { short: -2.0, average: 0, long: 3.0 };

  /* Minsta storleksindex vars innerben racker till kundens langd.
     -1 = tabellen saknar langdmatt (toppar, skor) och langden spelar ingen roll. */
  function legFloorIndex(sizes, height, bias) {
    if (!height) return -1;
    var need = height * LEG_RATIO + (bias || 0);
    var longest = -1;
    for (var i = 0; i < sizes.length; i++) {
      var ins = sizes[i].inseam;
      if (typeof ins !== 'number' || ins <= 0) continue;
      longest = i;
      if (ins >= need - LEG_TOL) return i;
    }
    /* Langre ben an hela tabellen: ge den langsta som finns i stallet for ingenting. */
    return longest;
  }

  function recommend(sizeSet, answers) {
    var m = estimateBody(answers);
    var dims = sizeSet.dimensions || [{ key: 'waist', weight: 0.55 }, { key: 'hip', weight: 0.45 }];
    var scored = scoreSizes(sizeSet.sizes, m, FIT_OFFSET[answers.fit] || 0, dims);
    if (!scored.length) return null;

    var best = scored[0];
    var runnerUp = scored[1] || null;
    var between = runnerUp ? (runnerUp.penalty - best.penalty) < 0.45 : false;

    /* Golvet appliceras sist, sa girth-poangen alltid ar rakt raknad. Hojs storleken
       av langden ar valet inte langre "mellan tva" - det ar ett medvetet val. */
    var legRaised = false;
    var floorIdx = legFloorIndex(sizeSet.sizes, answers.height, LEG_BIAS[answers.legs] || 0);
    if (floorIdx > -1 && best.index < floorIdx) {
      for (var f = 0; f < scored.length; f++) {
        if (scored[f].index === floorIdx) {
          best = scored[f];
          between = false;
          runnerUp = null;
          legRaised = true;
          break;
        }
      }
    }

    return {
      size: best.size.label,
      penalty: best.penalty,
      /* Hela raden folj med, sa benlangdskontrollen kommer at plaggets innerben. */
      sizeRow: best.size,
      /* Langden valde storleken - da ar girth-poangen ointressant. */
      outOfRange: legRaised ? false : best.penalty > 2.6,
      legRaised: legRaised,
      between: between,
      alternative: between ? runnerUp.size.label : null,
      alternativeIsLarger: between ? runnerUp.index > best.index : false,
      measurements: { waist: Math.round(m.waist), hip: Math.round(m.hip), bust: Math.round(m.bust) }
    };
  }

  /* ------------------------------------------------------------------ *
   * Storage                                                             *
   * ------------------------------------------------------------------ */
  function loadProfile() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function saveProfile(answers) {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(answers)); } catch (e) {}
  }
  function clearProfile() {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * Helpers                                                             *
   * ------------------------------------------------------------------ */
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function fmt(str, vars) {
    return String(str).replace(/\{(\w+)\}/g, function (_, k) { return vars[k] != null ? vars[k] : ''; });
  }
  /* "2XL", "XXL" och " 2xl " ar samma storlek. Utan normalisering hittade vi
     ingen variant och knappen pastod att storleken var slut. */
  function normSize(v) {
    var s = String(v == null ? '' : v).trim().toUpperCase().replace(/\s+/g, '');
    var m = s.match(/^(X{2,})(S|L)$/);
    if (m) return m[1].length + 'X' + m[2];
    return s;
  }
  function escapeHtml(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function cmToFtIn(cm) {
    var inches = Math.round(cm / 2.54);
    return Math.floor(inches / 12) + "'" + (inches % 12) + '"';
  }
  function kgToLb(kg) { return Math.round(kg * 2.20462); }

  var ICON_PENCIL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>';
  var ICON_ARROW = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
  var ICON_CHECK = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
  var ICON_RULER = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="8" width="20" height="8" rx="1.5"/><path d="M6 8v3M10 8v5M14 8v3M18 8v5"/></svg>';
  var ICON_CROSS = '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>';

  /* ------------------------------------------------------------------ *
   * Quiz instance                                                       *
   * ------------------------------------------------------------------ */
  function Quiz(config) {
    this.cfg = config;
    this.t = Object.assign({}, SV, config.strings || {});
    this.sizeSet = config.sizeSet;
    this.stepIds = (this.sizeSet.steps || ['height', 'weight', 'age', 'waist', 'hips', 'fit']).slice();
    this.units = { length: config.defaultUnits && config.defaultUnits.length || 'cm',
                   weight: config.defaultUnits && config.defaultUnits.weight || 'kg' };

    var saved = loadProfile();
    this.answers = saved || {};
    this.cursor = saved ? this.stepIds.length : 0; // returning shoppers land straight on the result
    this.draft = {};
    this.build();
    this.syncTrigger();
  }

  Quiz.prototype.build = function () {
    var self = this;

    this.overlay = el('div', 'on-sq on-sq-overlay');
    this.overlay.setAttribute('data-open', 'false');
    this.overlay.addEventListener('click', function () { self.close(); });

    this.panel = el('div', 'on-sq on-sq-panel');
    this.panel.setAttribute('data-open', 'false');
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    this.panel.setAttribute('aria-label', this.t.trigger);

    var head = el('div', 'on-sq-head');
    if (this.cfg.logo) {
      var logo = el('span', 'on-sq-brand on-sq-brand--logo');
      var img = document.createElement('img');
      img.src = this.cfg.logo;
      img.alt = this.cfg.brandLabel || '';
      img.loading = 'lazy';
      logo.appendChild(img);
      head.appendChild(logo);
    } else {
      head.appendChild(el('span', 'on-sq-brand', this.cfg.brandLabel || this.t.brand));
    }
    var close = el('button', 'on-sq-close', ICON_CROSS);
    close.type = 'button';
    close.setAttribute('aria-label', this.t.close);
    close.addEventListener('click', function () { self.close(); });
    head.appendChild(close);

    this.body = el('div', 'on-sq-body');

    this.foot = el('div', 'on-sq-foot');
    var privacy = el('span', null, this.cfg.privacyUrl
      ? '<a href="' + this.cfg.privacyUrl + '">' + this.t.privacy + '</a>'
      : '');
    this.progress = el('span', 'on-sq-progress');
    this.topBtn = el('button', 'on-sq-top', this.t.back_to_top);
    this.topBtn.type = 'button';
    this.topBtn.addEventListener('click', function () { self.body.scrollTo({ top: 0 }); });
    this.foot.appendChild(privacy);
    this.foot.appendChild(this.progress);
    this.foot.appendChild(this.topBtn);

    this.panel.appendChild(head);
    this.panel.appendChild(this.body);
    this.panel.appendChild(this.foot);

    document.body.appendChild(this.overlay);
    document.body.appendChild(this.panel);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.panel.getAttribute('data-open') === 'true') self.close();
    });
  };

  Quiz.prototype.open = function () {
    this.lastFocus = document.activeElement;
    this.overlay.setAttribute('data-open', 'true');
    this.panel.setAttribute('data-open', 'true');
    document.documentElement.style.overflow = 'hidden';
    this.render();
    var first = this.panel.querySelector('.on-sq-close');
    if (first) first.focus();
  };

  Quiz.prototype.close = function () {
    this.overlay.setAttribute('data-open', 'false');
    this.panel.setAttribute('data-open', 'false');
    document.documentElement.style.overflow = '';
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  /* ---------- rendering ---------- */

  Quiz.prototype.render = function () {
    var self = this;
    this.body.innerHTML = '';

    if (this.cursor === 0) this.body.appendChild(this.renderIntro());

    if (this.cursor > 0) {
      var rows = el('div', 'on-sq-rows');
      for (var i = 0; i < this.cursor && i < this.stepIds.length; i++) {
        rows.appendChild(this.renderRow(this.stepIds[i], i));
      }
      this.body.appendChild(rows);
    }

    if (this.cursor < this.stepIds.length) {
      this.body.appendChild(this.renderStep(this.stepIds[this.cursor]));
      this.progress.textContent = (this.cursor + 1) + ' / ' + (this.stepIds.length + 1);
    } else {
      this.body.appendChild(this.renderResult());
      this.progress.textContent = (this.stepIds.length + 1) + ' / ' + (this.stepIds.length + 1);
    }

    this.topBtn.style.visibility = this.cursor > 1 ? 'visible' : 'hidden';
    window.requestAnimationFrame(function () {
      var step = self.body.querySelector('.on-sq-step, .on-sq-result');
      if (step && self.cursor > 0) step.scrollIntoView({ block: 'start' });
    });
  };

  Quiz.prototype.renderIntro = function () {
    var wrap = document.createDocumentFragment();
    var intro = el('div', 'on-sq-intro');
    intro.appendChild(el('div', 'on-sq-intro-text',
      '<h2>' + this.t.intro_title + '</h2><p>' + this.t.intro_body + '</p>'));
    var img = el('div', 'on-sq-intro-img');
    if (this.cfg.introImage) img.style.backgroundImage = 'url("' + this.cfg.introImage + '")';
    intro.appendChild(img);
    wrap.appendChild(intro);

    var strip = el('div', 'on-sq-strip');
    if (this.cfg.product && this.cfg.product.image) {
      var pi = new Image();
      pi.src = this.cfg.product.image;
      pi.alt = '';
      strip.appendChild(pi);
    } else {
      strip.appendChild(el('span', 'on-sq-strip-fallback'));
    }
    strip.appendChild(el('span', null, this.t.strip));
    wrap.appendChild(strip);

    // Returtexten hor hemma har, innan kunden svarar - inte efter att storleken
    // redan ar satt. Da ar den ett skal att gora quizet, inte en efterhandsnot.
    var note = this.cfg.note || this.t.note;
    if (note) wrap.appendChild(el('p', 'on-sq-note', escapeHtml(note)));

    return wrap;
  };

  Quiz.prototype.renderRow = function (id, index) {
    var self = this;
    var row = el('button', 'on-sq-row');
    row.type = 'button';
    row.appendChild(el('span', 'on-sq-row-label', this.t[id]));
    row.appendChild(el('span', 'on-sq-row-value', this.summaryValue(id) + ICON_PENCIL));
    row.addEventListener('click', function () {
      self.cursor = index;
      self.draft = {};
      self.render();
    });
    return row;
  };

  Quiz.prototype.summaryValue = function (id) {
    var a = this.answers, t = this.t;
    switch (id) {
      case 'height':
        return fmt(t.height_value, { value: this.units.length === 'cm'
          ? a.height + ' ' + t.cm : cmToFtIn(a.height) });
      case 'weight':
        return fmt(t.weight_value, { value: this.units.weight === 'kg'
          ? a.weight + ' ' + t.kg : kgToLb(a.weight) + ' ' + t.lb });
      case 'age': return fmt(t.age_value, { value: a.age });
      case 'waist': return t['waist_' + a.waist];
      case 'hips': return t['hips_' + a.hips];
      case 'legs': return t['legs_' + a.legs];
      case 'bust': return t['bust_' + a.bust];
      case 'fit': return t['fit_' + (a.fit === 'tight' ? 'tight' : a.fit === 'loose' ? 'loose' : 'regular')];
      default: return '';
    }
  };

  Quiz.prototype.renderStep = function (id) {
    var map = {
      height: this.stepHeight, weight: this.stepWeight, age: this.stepAge,
      waist: this.stepShape('waist', ['flat', 'average', 'round']),
      hips: this.stepShape('hips', ['narrow', 'average', 'wide']),
      legs: this.stepShape('legs', ['short', 'average', 'long']),
      bust: this.stepShape('bust', ['small', 'average', 'full']),
      fit: this.stepFit
    };
    return map[id].call(this);
  };

  Quiz.prototype.nextButton = function (enabled, onNext, isLast) {
    var self = this;
    var last = isLast != null ? isLast : (this.cursor === this.stepIds.length - 1);
    var btn = el('button', 'on-sq-next',
      '<span>' + (last ? this.t.done : this.t.next) + '</span>' + (last ? ICON_CHECK : ICON_ARROW));
    btn.type = 'button';
    btn.disabled = !enabled;
    btn.addEventListener('click', function () {
      onNext();
      saveProfile(self.answers);
      self.cursor = Math.min(self.cursor + 1, self.stepIds.length);
      // when editing an earlier answer, jump straight back to the result
      if (self.everCompleted) self.cursor = self.stepIds.length;
      self.draft = {};
      self.render();
      self.syncTrigger();
    });
    return btn;
  };

  Quiz.prototype.stepHeight = function () {
    var self = this;
    var wrap = el('div', 'on-sq-step');
    var head = el('div', 'on-sq-qhead');
    head.appendChild(el('h3', null, this.t.height_q));

    var units = el('div', 'on-sq-units');
    ['cm', 'ft'].forEach(function (u) {
      var b = el('button', null, self.t[u]);
      b.type = 'button';
      b.setAttribute('aria-pressed', String(self.units.length === u));
      b.addEventListener('click', function () { self.units.length = u; self.render(); });
      units.appendChild(b);
    });
    head.appendChild(units);
    wrap.appendChild(head);

    var value = this.answers.height || 168;
    var readout = el('div', 'on-sq-readout');
    var prev = el('span', 'on-sq-ghost');
    var now = el('span', 'on-sq-now');
    var next = el('span', 'on-sq-ghost');
    readout.appendChild(prev); readout.appendChild(now); readout.appendChild(next);
    wrap.appendChild(readout);

    var ruler = el('div', 'on-sq-ruler');
    ruler.appendChild(el('div', 'on-sq-ticks'));
    var range = el('input');
    range.type = 'range';
    range.className = 'on-sq-range';
    range.min = 140; range.max = 200; range.step = 1; range.value = value;
    range.setAttribute('aria-label', this.t.height_q);
    ruler.appendChild(range);
    wrap.appendChild(ruler);

    function paint() {
      var v = parseInt(range.value, 10);
      var show = function (n) { return self.units.length === 'cm' ? n + ' ' + self.t.cm : cmToFtIn(n); };
      prev.textContent = v > 140 ? show(v - 5) : '';
      now.textContent = show(v);
      next.textContent = v < 200 ? show(v + 5) : '';
      range.setAttribute('aria-valuetext', show(v));
    }
    range.addEventListener('input', paint);
    paint();

    wrap.appendChild(this.nextButton(true, function () {
      self.answers.height = parseInt(range.value, 10);
    }));
    return wrap;
  };

  Quiz.prototype.stepWeight = function () {
    var self = this;
    var wrap = el('div', 'on-sq-step');
    var head = el('div', 'on-sq-qhead');
    head.appendChild(el('h3', null, this.t.weight_q));

    var units = el('div', 'on-sq-units');
    ['kg', 'lb'].forEach(function (u) {
      var b = el('button', null, self.t[u]);
      b.type = 'button';
      b.setAttribute('aria-pressed', String(self.units.weight === u));
      b.addEventListener('click', function () { self.units.weight = u; self.render(); });
      units.appendChild(b);
    });
    head.appendChild(units);
    wrap.appendChild(head);

    var readout = el('div', 'on-sq-readout');
    var prev = el('span', 'on-sq-ghost');
    var now = el('span', 'on-sq-now');
    var next = el('span', 'on-sq-ghost');
    readout.appendChild(prev); readout.appendChild(now); readout.appendChild(next);
    wrap.appendChild(readout);

    var ruler = el('div', 'on-sq-ruler');
    ruler.appendChild(el('div', 'on-sq-ticks'));
    var range = el('input');
    range.type = 'range';
    range.className = 'on-sq-range';
    range.min = 40; range.max = 140; range.step = 1;
    range.value = this.answers.weight || 70;
    range.setAttribute('aria-label', this.t.weight_q);
    ruler.appendChild(range);
    wrap.appendChild(ruler);

    function paint() {
      var v = parseInt(range.value, 10);
      var show = function (n) { return self.units.weight === 'kg' ? n + ' ' + self.t.kg : kgToLb(n) + ' ' + self.t.lb; };
      prev.textContent = v > 40 ? show(v - 5) : '';
      now.textContent = show(v);
      next.textContent = v < 140 ? show(v + 5) : '';
      range.setAttribute('aria-valuetext', show(v));
    }
    range.addEventListener('input', paint);
    paint();

    wrap.appendChild(this.nextButton(true, function () {
      self.answers.weight = parseInt(range.value, 10);
    }));
    return wrap;
  };

  Quiz.prototype.stepAge = function () {
    var self = this;
    var wrap = el('div', 'on-sq-step');
    wrap.appendChild(el('h3', null, this.t.age_q));
    wrap.appendChild(el('p', 'on-sq-help', this.t.age_help));

    var picked = this.draft.age || this.answers.age || null;
    var pills = el('div', 'on-sq-pills');
    var next;
    Object.keys(AGE_MID).forEach(function (band) {
      var b = el('button', 'on-sq-pill', band);
      b.type = 'button';
      b.setAttribute('aria-pressed', String(picked === band));
      b.addEventListener('click', function () {
        picked = band;
        Array.prototype.forEach.call(pills.children, function (c) {
          c.setAttribute('aria-pressed', String(c === b));
        });
        next.disabled = false;
      });
      pills.appendChild(b);
    });
    wrap.appendChild(pills);

    next = this.nextButton(!!picked, function () { self.answers.age = picked; });
    wrap.appendChild(next);
    return wrap;
  };

  Quiz.prototype.stepShape = function (group, keys) {
    return function () {
      var self = this;
      var wrap = el('div', 'on-sq-step');
      wrap.appendChild(el('h3', null, this.t[group + '_q']));

      var picked = this.answers[group] || 'average';
      var choices = el('div', 'on-sq-choices');
      keys.forEach(function (key) {
        var b = el('button', 'on-sq-choice',
          figure(group, key) + '<span>' + self.t[group + '_' + key] + '</span>');
        b.type = 'button';
        b.setAttribute('aria-pressed', String(picked === key));
        b.addEventListener('click', function () {
          picked = key;
          Array.prototype.forEach.call(choices.children, function (c) {
            c.setAttribute('aria-pressed', String(c === b));
          });
        });
        choices.appendChild(b);
      });
      wrap.appendChild(choices);

      wrap.appendChild(this.nextButton(true, function () { self.answers[group] = picked; }));
      return wrap;
    };
  };

  Quiz.prototype.stepFit = function () {
    var self = this;
    var order = ['tight', 'regular', 'loose'];
    var wrap = el('div', 'on-sq-step');
    wrap.appendChild(el('h3', null, this.t.fit_q));

    var current = order.indexOf(this.answers.fit || 'regular');
    if (current < 0) current = 1;

    var box = el('div', 'on-sq-fit');
    var range = el('input');
    range.type = 'range';
    range.className = 'on-sq-fitrange';
    range.min = 0; range.max = 2; range.step = 1; range.value = current;
    range.setAttribute('aria-label', this.t.fit_q);
    box.appendChild(range);

    var labels = el('div', 'on-sq-fit-labels');
    var spans = order.map(function (key) {
      var s = el('span', null, self.t['fit_' + key]);
      labels.appendChild(s);
      return s;
    });
    box.appendChild(labels);
    wrap.appendChild(box);

    function paint() {
      var i = parseInt(range.value, 10);
      spans.forEach(function (s, n) { s.setAttribute('data-active', String(n === i)); });
      range.setAttribute('aria-valuetext', self.t['fit_' + order[i]]);
    }
    range.addEventListener('input', paint);
    paint();

    wrap.appendChild(this.nextButton(true, function () {
      self.answers.fit = order[parseInt(range.value, 10)];
    }, true));
    return wrap;
  };

  /* ---------- result ---------- */

  Quiz.prototype.renderResult = function () {
    var self = this;
    this.everCompleted = true;

    var rec = recommend(this.sizeSet, this.answers);
    this.result = rec;

    var wrap = el('div', 'on-sq-result');
    wrap.appendChild(el('h3', null, this.t.result_title));
    wrap.appendChild(el('p', null, this.t.result_sub));

    /* Ingen varningstext langre, aven nar kunden ligger utanfor tabellen. Vi
       visar narmaste storlek rakt av. En text som sager att plagget kanske inte
       racker till far kunden att avbryta kopet, och storleksspannet ar redan
       synligt i storleksguiden for den som vill kontrollera. */
    if (!rec) {
      var guideA = el('button', 'on-sq-link', this.t.size_guide);
      guideA.type = 'button';
      guideA.addEventListener('click', function () { self.openSizeGuide(); });
      wrap.appendChild(guideA);
      return wrap;
    }

    /* Rubriken far hela kortets bredd pa egen rad, bild och storlek under.
       Bredvid bilden rackte utrymmet inte till pa svenska ("...rekommenderar
       vi" brots till tva rader) - har far alla 12 sprak plats pa en rad. */
    var card = el('div', 'on-sq-card');
    card.appendChild(el('p', 'on-sq-card-label', this.t.result_for));
    if (this.cfg.product && this.cfg.product.image) {
      var img = new Image();
      img.src = this.cfg.product.image;
      img.alt = this.cfg.product.title || '';
      img.className = 'on-sq-card-img';
      card.appendChild(img);
    } else {
      card.appendChild(el('div', 'on-sq-card-img'));
    }
    card.appendChild(el('strong', 'on-sq-card-size', escapeHtml(this.sizeLabel(rec.size))));
    wrap.appendChild(card);

    /* Ingen "mellan tva storlekar"-text och ingen benlangdsvarning langre.
       Tvivel saljer inte, och passformen ar redan besvarad i sista fragan.
       Benlangden ligger i stallet i uttrakningen (se recommend). */

    var prod = this.cfg.product || {};
    var sizeIdx = typeof prod.sizeIndex === 'number' ? prod.sizeIndex : 0;
    var names = prod.optionNames || [];
    // Varianter i den rekommenderade storleken. Normaliserat, sa "2XL" i
    // tabellen matchar "XXL" bland varianterna.
    var variants = (prod.variants || []).filter(function (v) {
      return normSize(v.size) === normSize(rec.size);
    });

    var label = this.sizeLabel(rec.size);
    var toCart = this.cfg.resultAction === 'cart';

    if (toCart && variants.length) {
      // Kunden ska kunna valja ALLA optioner har, inte bara farg - annars
      // hamnar fel benstil i varukorgen.
      var otherIdx = [];
      for (var i = 0; i < names.length; i++) if (i !== sizeIdx) otherIdx.push(i);

      var picked = {};
      var firstOk = variants.filter(function (v) { return v.available; })[0] || variants[0];
      otherIdx.forEach(function (i) { picked[i] = firstOk.options[i]; });

      var current = function () {
        return variants.filter(function (v) {
          return otherIdx.every(function (i) { return v.options[i] === picked[i]; });
        })[0] || null;
      };

      var atc = el('button', 'on-sq-atc');
      atc.type = 'button';

      var refresh = function () {
        var v = current();
        atc.disabled = !v || !v.available;
        atc.textContent = v && v.available
          ? fmt(self.t.add, { size: label })
          : fmt(self.t.sold_out, { size: label });
      };

      // Bild-swatchar med samma kallor som variantvaljaren, i stallet for
      // dropdowns. Kunden ska kanna igen sig fran produktsidan.
      var meta = {};
      (prod.optionValues || []).forEach(function (o) { meta[o.index] = o.values || []; });
      var chips = {};

      otherIdx.forEach(function (i) {
        var values = [];
        variants.forEach(function (v) {
          if (values.indexOf(v.options[i]) === -1) values.push(v.options[i]);
        });
        if (values.length < 2) return;

        var box = el('div', 'on-sq-opt');
        var head = el('div', 'on-sq-opt-head');
        head.appendChild(el('span', 'on-sq-opt-name', escapeHtml(names[i] || self.t.color)));
        var valueOut = el('span', 'on-sq-opt-value', escapeHtml(picked[i]));
        head.appendChild(valueOut);
        box.appendChild(head);

        var row = el('div', 'on-sq-opt-row');
        chips[i] = [];
        values.forEach(function (val) {
          var info = meta[i].filter(function (m) { return m.name === val; })[0] || {};
          var inStock = variants.some(function (v) { return v.options[i] === val && v.available; });
          var b = el('button', 'on-sq-chip');
          b.type = 'button';
          b.title = val;
          b.setAttribute('aria-label', val);
          if (info.image) {
            b.classList.add('on-sq-chip--img');
            var im = new Image();
            im.src = info.image;
            im.alt = val;
            im.loading = 'lazy';
            b.appendChild(im);
          } else if (info.hex) {
            b.classList.add('on-sq-chip--dot');
            b.appendChild(el('span', 'on-sq-chip-dot')).style.background = info.hex;
          } else {
            b.classList.add('on-sq-chip--text');
            b.appendChild(el('span', null, escapeHtml(val)));
          }
          if (!inStock) b.classList.add('is-unavailable');
          b.setAttribute('aria-pressed', String(val === picked[i]));
          b.addEventListener('click', function () {
            picked[i] = val;
            valueOut.textContent = val;
            chips[i].forEach(function (c) { c.setAttribute('aria-pressed', String(c === b)); });
            refresh();
          });
          chips[i].push(b);
          row.appendChild(b);
        });
        box.appendChild(row);
        wrap.appendChild(box);
      });

      refresh();
      atc.addEventListener('click', function () {
        var v = current();
        if (v) self.addToCart(v.id, atc, wrap);
      });
      wrap.appendChild(atc);
    } else {
      /* Standard: skicka tillbaka kunden till produktsidan med storleken vald.
         Da valjer hen farg och benstil i den vanliga variantvaljaren, dar
         bilder och lagerstatus finns, och gar vidare darifran. */
      var pick = el('button', 'on-sq-atc', fmt(this.t.pick_size || 'Välj {size}', { size: label }));
      pick.type = 'button';
      pick.addEventListener('click', function () {
        self.close();
        setTimeout(function () {
          var form = document.querySelector('product-form-component, form[action*="/cart/add"]');
          var target = form || document.querySelector('.on-vp-wrap, variant-picker');
          if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
      });
      wrap.appendChild(pick);
    }

    if (this.hasSizeGuide()) {
      var guide = el('button', 'on-sq-link', this.t.size_guide);
      guide.type = 'button';
      guide.addEventListener('click', function () { self.openSizeGuide(); });
      wrap.appendChild(guide);
    }

    var reset = el('button', 'on-sq-link', this.t.restart);
    reset.type = 'button';
    if (this.hasSizeGuide()) reset.style.marginLeft = '16px';
    reset.addEventListener('click', function () {
      clearProfile();
      self.answers = {};
      self.cursor = 0;
      self.everCompleted = false;
      self.render();
      self.syncTrigger();
    });
    wrap.appendChild(reset);

    // let the product page preselect the variant
    document.dispatchEvent(new CustomEvent('on:size-quiz:result', {
      detail: {
        size: rec.size,
        alternative: rec.alternative,
        measurements: rec.measurements,
        variantId: (variants.filter(function (v) { return v.available; })[0] || variants[0] || {}).id || null,
        productId: this.cfg.product && this.cfg.product.id
      }
    }));

    return wrap;
  };

  /* Lade forut bara till raden och stangde panelen. Butikens /cart/add.js tar
     over tva sekunder, och utan besked hann kunden tro att knappen var trasig -
     dessutom fick temat aldrig veta nagot, sa varukorgssiffran i headern stod
     kvar. Nu: direkt "lagger till"-lage, sedan samma cart:update-handelse och
     sections-nyckel som resten av temat anvander, och drawern oppnas. */
  Quiz.prototype.addToCart = function (variantId, button, wrap) {
    var self = this;
    if (!variantId) return;
    var original = button.textContent;
    button.disabled = true;
    button.classList.add('is-busy');
    button.textContent = self.t.adding || original;

    var body = { items: [{ id: Number(variantId), quantity: 1 }] };
    var cic = document.querySelector('cart-items-component');
    var sid = cic ? (cic.getAttribute('section-id') || cic.getAttribute('data-section-id')) : null;
    if (sid) { body.sections = sid; body.sections_url = window.location.pathname; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('cart');
      return r.json();
    }).then(function (added) {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          button.classList.remove('is-busy');
          button.textContent = self.t.added;
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'on-size-quiz',
              data: { source: 'on-size-quiz', itemCount: cart.item_count, sections: added ? added.sections : null }
            }
          }));
          document.dispatchEvent(new CustomEvent('on:size-quiz:added', { detail: { variantId: variantId } }));
          window.setTimeout(function () {
            self.close();
            var drawer = document.querySelector('cart-drawer-component');
            if (drawer && typeof drawer.open === 'function') { try { drawer.open(); } catch (e) {} }
          }, 550);
        });
    }).catch(function () {
      button.disabled = false;
      button.classList.remove('is-busy');
      button.textContent = original;
      var err = wrap.querySelector('.on-sq-error');
      if (!err) {
        err = el('div', 'on-sq-error', self.t.cart_error);
        wrap.appendChild(err);
      }
    });
  };

  /* Sidans egen storleksguide. Blocket Storleksguide exponerar [data-sg-open],
     mini-guiden [data-sizepop]. Utan bade lank och knapp visas ingen lank alls -
     forr renderades den alltid och gjorde ingenting nar den klickades. */
  /* Bara Storleksguide - den kunden bygger sjalv i editorn. querySelector
     med en lista hade tagit den forsta i DOM-ordning, vilket kunde bli
     mini-guiden [data-sizepop]. Darfor provas selektorerna i tur och ordning. */
  var GUIDE_SELECTORS = ['[data-sg-open]', '[data-on-size-guide-trigger]'];

  Quiz.prototype.guideTrigger = function () {
    for (var i = 0; i < GUIDE_SELECTORS.length; i++) {
      var found = document.querySelector(GUIDE_SELECTORS[i]);
      if (found) return found;
    }
    return null;
  };

  Quiz.prototype.hasSizeGuide = function () {
    return !!(this.cfg.sizeGuideUrl || this.guideTrigger());
  };

  Quiz.prototype.openSizeGuide = function () {
    if (this.cfg.sizeGuideUrl) { window.location.href = this.cfg.sizeGuideUrl; return; }
    var trigger = this.guideTrigger();
    if (!trigger) return;
    this.close();
    // Guiden ar en <dialog>; ge panelens stangning en tick sa den inte staller
    // sig framfor modalen.
    setTimeout(function () { trigger.click(); }, 60);
  };

  /* "XL • 44" - samma siffra som variantknapparna visar, ur produktens egen
     tabell via on-size-secondary. Saknas siffran visas bara bokstaven. */
  Quiz.prototype.sizeLabel = function (size) {
    var rows = (this.sizeSet && this.sizeSet.sizes) || [];
    for (var i = 0; i < rows.length; i++) {
      if (normSize(rows[i].label) === normSize(size) && rows[i].secondary) {
        return size + ' • ' + rows[i].secondary;
      }
    }
    return size;
  };

  /* ---------- trigger button ---------- */

  Quiz.prototype.syncTrigger = function () {
    var self = this;
    var buttons = document.querySelectorAll('[data-on-sq-trigger]');
    var known = null;
    if (loadProfile() && this.stepIds.every(function (id) { return self.answers[id] != null; })) {
      var rec = recommend(this.sizeSet, this.answers);
      // Aven utanfor tabellen: knappen ska visa samma storlek som panelen gor,
      // annars sag knappen olika saker pa tva produkter for samma kund.
      if (rec) known = rec.size;
    }
    Array.prototype.forEach.call(buttons, function (b) {
      b.setAttribute('data-has-result', String(!!known));
      var label = b.querySelector('[data-on-sq-label]');
      if (!label) return;
      // Knapptexten kan vara vald i temaeditorn. Spara den serverrenderade
      // texten och atergaa till DEN, inte till standardstrangen, nar det inte
      // finns nagot resultat att visa.
      if (b.dataset.baseLabel == null) b.dataset.baseLabel = label.textContent.trim() || self.t.trigger;
      label.textContent = known ? fmt(self.t.trigger_known, { size: self.sizeLabel(known) }) : b.dataset.baseLabel;
    });
    if (known) {
      document.dispatchEvent(new CustomEvent('on:size-quiz:known', { detail: { size: known } }));
    }
  };

  /* ------------------------------------------------------------------ *
   * Boot                                                                *
   * ------------------------------------------------------------------ */
  var OnSizeQuiz = {
    instance: null,
    init: function (config) {
      if (!config || !config.sizeSet || !config.sizeSet.sizes || !config.sizeSet.sizes.length) {
        console.warn('[on-size-quiz] missing size chart for this product, quiz not mounted');
        return null;
      }
      var quiz = new Quiz(config);
      this.instance = quiz;
      var triggers = document.querySelectorAll('[data-on-sq-trigger]');
      Array.prototype.forEach.call(triggers, function (b) {
        b.addEventListener('click', function () { quiz.open(); });
      });
      return quiz;
    },
    // exposed for tuning and for unit tests
    estimateBody: estimateBody,
    recommend: recommend,
    clearProfile: clearProfile,
    getProfile: loadProfile
  };

  window.OnSizeQuiz = OnSizeQuiz;
})(window, document);
